<template>
  <div class="seo-page blog">
    <header class="blog__header">
      <h1>Blog</h1>
      <p>Steam news, our players' playtime trends, and guides.</p>
    </header>

    <section class="blog-list" aria-label="Posts">
      <article v-for="post in posts" :key="post.slug" class="blog-row">
        <router-link :to="post.path" class="blog-row__link">
          <div class="blog-row__thumb">
            <img
              v-if="post.image && !failedImages[post.slug]"
              :src="post.image"
              alt=""
              loading="lazy"
              @error="failedImages[post.slug] = true"
            >
            <span v-else class="blog-row__thumb-tag">{{ post.tag }}</span>
          </div>
          <div class="blog-row__body">
            <div class="blog-row__meta">
              <span class="blog-row__tag">{{ post.tag }}</span>
              <span>{{ post.dateLabel }}</span>
            </div>
            <h2 class="blog-row__title">{{ post.title }}</h2>
            <p class="blog-row__excerpt">{{ post.excerpt }}</p>
          </div>
        </router-link>
      </article>
    </section>

    <section class="blog-tools">
      <h2>Tools</h2>
      <ul class="blog-tools__list">
        <li v-for="tool in tools" :key="tool.slug">
          <router-link :to="tool.path">{{ tool.title }}</router-link>
        </li>
      </ul>
    </section>
  </div>
</template>

<script>
import { applyMeta, pageStructuredData } from '../utils/meta';
import { SEO_PAGES } from '../utils/seoPages';
import { newsPosts, blogCard } from '../utils/newsPosts';

const META = {
  title: 'steamplayti.me Tools & Blog | Steam Tracking Tools, Guides & Sale Highlights',
  description: 'Every steamplayti.me tool in one place — playtime tracker, play history, wasted hours, replay, charts and more — plus gaming news and Steam sale spotlights.',
  keywords: 'steamplayti.me tools, Steam playtime tracker, Steam play history, Steam wasted hours, Steam Deck news, Steam sale tips, gaming blog',
  canonical: 'https://steamplayti.me/blog/',
  ogTitle: 'steamplayti.me Blog',
  ogDescription: 'Guides, sale spotlights, and tracking tips to make the most of steamplayti.me.',
  ogUrl: 'https://steamplayti.me/blog/',
  twitterTitle: 'steamplayti.me Blog',
  twitterDescription: 'Steam news, Deck-friendly deals, and tracking tricks direct from the steamplayti.me crew.',
  structuredData: pageStructuredData({
    name: 'Steam Tools & Blog',
    description: 'Every steamplayti.me tool in one place, plus Steam guides and sale spotlights.',
    canonical: 'https://steamplayti.me/blog/',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tools & Blog', path: 'https://steamplayti.me/blog/' },
    ],
  }),
};

const POSTS = [
  {
    slug: 'most-played-steam-games-september-2026',
    title: 'The Most-Played Steam Games of September 2026',
    excerpt: 'Real hours from 1,205 tracked players: Counter-Strike 2 on top, WARDOGS and Valheim surging, and how long a typical play day lasts.',
    tag: 'Playtime Report',
    dateLabel: 'October 20, 2026',
    path: '/blog/most-played-steam-games-september-2026/',
  },
  {
    slug: 'game-pass-steam-deck-xodus',
    title: 'Xbox Game Pass Games Are Coming to Steam Deck',
    excerpt: 'The Xodus project already handles Xbox sign-in, downloads, and MSIXVC decryption - the Xbox Gaming Runtime is the last hurdle before native Game Pass on Deck.',
    tag: 'Steam Deck',
    dateLabel: 'August 14, 2026',
    path: '/blog/game-pass-steam-deck-xodus/',
  },
  {
    slug: 'steam-machine-price',
    title: 'Steam Machine Price and Release Date Announced',
    excerpt: 'Valve reveals the Steam Machine: pricing from $1,049, an AMD Zen 4 CPU with RDNA 3 graphics, a June 29 launch, and a lottery-based reservation queue.',
    tag: 'Hardware',
    dateLabel: 'June 22, 2026',
    path: '/blog/steam-machine-price/',
  },
  {
    slug: 'xbox-game-pass-price-update',
    title: 'Xbox Game Pass Price Hike and Plan Renames',
    excerpt: 'Microsoft raises the Ultimate tier to $29.99, renames Core and Standard, and folds Ubisoft+ Classics into Game Pass.',
    tag: 'Xbox',
    dateLabel: 'October 2, 2025',
    path: '/blog/xbox-game-pass-price-update/',
  },
  {
    slug: 'steam-deck-sale-autumn-2025',
    title: 'Steam Deck Sale 2025: Best Discounted Games',
    excerpt: 'Browse verified Deck-friendly picks from the Steam Autumn Sale 2025 and start tracking playtime the moment you buy.',
    tag: 'Steam Deck',
    dateLabel: 'Autumn Sale 2025',
    path: '/blog/steam-deck-sale/',
  },
];

export default {
  name: 'BlogListingPage',
  data() {
    return {
      tools: SEO_PAGES,
      failedImages: {},
      // News posts are always newer than the hand-written articles below them.
      posts: [...newsPosts().map(blogCard), ...POSTS],
    };
  },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
};
</script>

<style src="../styles/seo-page.css"></style>

<style scoped>
.blog {
  gap: 32px;
}

.blog__header h1 {
  font-size: 2rem;
  margin: 0 0 6px;
}

.blog__header p {
  margin: 0;
  color: var(--muted);
}

.blog-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.blog-row {
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 14px;
  transition: border-color 150ms ease, background 150ms ease;
}

.blog-row:hover {
  border-color: rgba(102, 192, 244, 0.4);
}

.blog-row:focus-within {
  outline: 2px solid var(--primary);
  outline-offset: 3px;
}

.blog-row__link {
  display: flex;
  gap: 20px;
  padding: 14px;
  color: inherit;
  text-decoration: none;
  outline: none;
}

.blog-row__thumb {
  flex: 0 0 230px;
  aspect-ratio: 460 / 215;
  border-radius: 10px;
  overflow: hidden;
  background: radial-gradient(circle at top right, rgba(102, 192, 244, 0.22), rgba(102, 192, 244, 0.05) 70%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.blog-row__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.blog-row__thumb-tag {
  color: var(--primary);
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  text-align: center;
  padding: 0 12px;
}

.blog-row__body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.blog-row__meta {
  display: flex;
  gap: 12px;
  font-size: 0.85rem;
  color: var(--muted);
}

.blog-row__tag {
  color: var(--primary);
  font-weight: 600;
}

.blog-row__title {
  font-size: 1.2rem;
  line-height: 1.35;
  margin: 0;
}

.blog-row__excerpt {
  margin: 0;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.blog-tools h2 {
  font-size: 1.1rem;
  margin: 0 0 12px;
}

.blog-tools__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.blog-tools__list a {
  display: inline-block;
  padding: 6px 12px;
  border: 1px solid var(--panel-border);
  border-radius: 999px;
  font-size: 0.9rem;
  color: var(--primary);
  text-decoration: none;
}

.blog-tools__list a:hover {
  border-color: rgba(102, 192, 244, 0.4);
}

@media (max-width: 640px) {
  .blog-row__link {
    flex-direction: column;
    gap: 12px;
  }

  .blog-row__thumb {
    flex-basis: auto;
    width: 100%;
  }

  .blog-row__thumb:has(.blog-row__thumb-tag) {
    display: none;
  }
}
</style>
